import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { ActivityAsyncAdapter } from './activity.adapter';
import { Metamodel } from 'app/model/base/model';
import _ from 'lodash';
import { IInvokeDataQualityActivity, InvokeDataQualityActivity, ModelConstants, DataQualityLinkedService, ReferenceAdapter } from 'shell-models-lib';
import { ValueAdapter } from '../value.adapter';
import { getFabricLsForParameterizedConnections } from '../utils';
import { IInvokeDataQualityActivityContext } from 'app/interfaces/copilot.pipeline.action.types';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(InvokeDataQualityActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            dataQualityId: '*',
            workspaceId: '*'
        }
    }
})
export class InvokeDataQualityActivityAdapter extends ActivityAsyncAdapter<InvokeDataQualityActivity, IInvokeDataQualityActivity, IInvokeDataQualityActivityContext> {
    public override async toFabricCopilotContext(activity: InvokeDataQualityActivity): Promise<IInvokeDataQualityActivityContext> {
        if (activity) {
            const activityContext: IInvokeDataQualityActivityContext = await super.toFabricCopilotContext(activity);
            activityContext.DataQualityId = ValueAdapter.toResource(activity, InvokeDataQualityActivity.DATAQUALITYID, true);
            activityContext.WorkspaceId = ValueAdapter.toResource(activity, InvokeDataQualityActivity.WORKSPACEID, true);
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IInvokeDataQualityActivityContext,
        model?: InvokeDataQualityActivity
    ): Promise<InvokeDataQualityActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            ValueAdapter.toModel(model, InvokeDataQualityActivity.DATAQUALITYID, context.DataQualityId);
            ValueAdapter.toModel(model, InvokeDataQualityActivity.WORKSPACEID, context.WorkspaceId);
        }
        return model;
    }

    protected async _toResource(activity: InvokeDataQualityActivity): Promise<IInvokeDataQualityActivity> {
        const modelDataQualityActivity = super._getCommonActivityProperties(
            activity, Metamodel.modelFor(InvokeDataQualityActivity).name) as IInvokeDataQualityActivity;

        modelDataQualityActivity.typeProperties = {
            dataQualityId: ValueAdapter.toResource(activity, InvokeDataQualityActivity.DATAQUALITYID),
            workspaceId: ValueAdapter.toResource(activity, InvokeDataQualityActivity.WORKSPACEID)
        };

        modelDataQualityActivity.linkedServiceName = this._toLinkedServiceReferenceModel(activity.dataQualityLinkedService,
            activity.linkedServiceParameters);

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelDataQualityActivity, parsed);
        }

        return modelDataQualityActivity;
    }

    protected async _toModel(modelActivity: IInvokeDataQualityActivity): Promise<InvokeDataQualityActivity> {
        const activity: InvokeDataQualityActivity = InvokeDataQualityActivity.MODEL.create<InvokeDataQualityActivity>(modelActivity.name, modelActivity.description);
        this._updatedCommonProperties(modelActivity, activity);

        ValueAdapter.toModel(activity, InvokeDataQualityActivity.DATAQUALITYID, modelActivity.typeProperties.dataQualityId);
        ValueAdapter.toModel(activity, InvokeDataQualityActivity.WORKSPACEID, modelActivity.typeProperties.workspaceId);
        this._setDataQualityConnection(activity, modelActivity);

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, InvokeDataQualityActivity.MODEL));
        return activity;
    }

    private _setDataQualityConnection(activity: InvokeDataQualityActivity, modelActivity: IInvokeDataQualityActivity): void {
        if (modelActivity.linkedServiceName !== undefined) {
            activity.dataQualityLinkedService = getFabricLsForParameterizedConnections(
                modelActivity.linkedServiceName,
                ModelConstants.dataQualityLinkedService,
                activity);

            if (!activity.dataQualityLinkedService) {
                activity.dataQualityLinkedService = ReferenceAdapter.toProxyModel<DataQualityLinkedService>(Metamodel.modelFor(DataQualityLinkedService),
                    modelActivity.linkedServiceName,
                    this.context);
            }
        }
    }
}
